import { useEffect, useState } from "react";
import { config } from "./Config.js";
import { GameStatus } from "./GameStatus.js";
import { miniMax } from "./Minimax.js";
import coinSound from "./coinDrop.mp3";
import Modal from "../common/Modal";
import "./Connect.scss";
import { isMobile } from "../../../utils/_helper.js";

const LEVELS = ["Easy", "Medium", "Hard"];

const Connect = ({
  feature,
  onDone,
  start = null,
  end = null,
  intro = "",
  share = {},
}) => {
  const [colSize] = useState(
    window.innerWidth < 480 ? config.numColsSmall : config.numColsBig
  );
  const [board, setBoard] = useState(
      Array.from({ length: config.numRows }, () =>
        Array.from({ length: colSize }, () => 0)
      )
    ),
    [playerWin, setPlayerWin] = useState(null),
    [gameDone, setGameDone] = useState(false),
    [difficulty, setDifficulty] = useState(1),
    [drawGame, setDrawGame] = useState(false),
    [header, setHeader] = useState(`It's your turn 😊`),
    [lock, setLock] = useState(false),
    [lastPlayer, setLastPlayer] = useState(0),
    [modal, setModal] = useState(true),
    [audio, setAudio] = useState(true),
    [modalType, setModalType] = useState("START"),
    [winningCoords, setWinningCoords] = useState([]),
    [playerMoves, setPlayersMoves] = useState(0),
    [initialTime, setInitialTime] = useState(new Date()),
    [stepTimer, setStepTimer] = useState({}),
    [playAgain, setPlayAgain] = useState(false),
    reset = () => {
      setPlayAgain(true);
      setBoard(
        Array.from({ length: config.numRows }, (_, row) =>
          Array.from({ length: colSize }, (_, col) => {
            document.getElementById(`${row}-${col}`).className = "";
            return 0;
          })
        )
      );
      setModal(true);
      setModalType("START");
      setLastPlayer(2);
      setDifficulty(1);
      setPlayerWin(null);
      setGameDone(false);
      setLock(false);
      setWinningCoords([]);
      setPlayersMoves(0);
      setInitialTime(new Date());
    };

  useEffect(() => {
    setModal(true);
    setModalType("START");
    setLastPlayer(2);
    setDifficulty(1);
    setPlayerWin(null);
    setGameDone(false);
    setLock(false);
    setPlayAgain(false);
  }, []);

  const playAudio = () => {
    new Audio(coinSound).play();
  };

  const toggleAudio = () => {
    setAudio(!audio);
  };

  const addCoinToMatrix = (board, col, value) => {
    for (let i = config.numRows - 1; i >= 0; i--) {
      if (!board[i][col]) {
        board[i][col] = value;
        value === 1
          ? (document.getElementById(`${i}-${col}`).className +=
              " connect4-player1")
          : (document.getElementById(`${i}-${col}`).className +=
              " connect4-player2");
        if (audio) {
          playAudio();
        }
        return true;
      }
    }
    return false;
  };

  const nextTurn = (currentPlayer) => {
    if (currentPlayer === 0 || currentPlayer === 2) {
      return 1;
    }
    return 2;
  };

  const computerStep = (board, AI_VALUE, difficulty1) => {
    const maxDepth = config["difficultyToMaxDepth"][difficulty1];
    const [depth, alpha, beta, isMaximizingPlayer] = [
      0,
      Number.NEGATIVE_INFINITY,
      Number.POSITIVE_INFINITY,
      true,
    ];
    const [maxScore, bestColumn] = miniMax(
      board,
      depth,
      alpha,
      beta,
      isMaximizingPlayer,
      2,
      1,
      maxDepth
    );
    if (bestColumn >= 0) {
      addCoinToMatrix(board, bestColumn, AI_VALUE);
    }
  };

  const afterGameOver = (currentBoard, gameStatus) => {
    let gameStats = {
      gameLevel: difficulty,
      status: gameStatus.status === 1 ? "Win" : "Lose",
      gameStepTime: stepTimer,
    };
    setGameDone(true);
    setHeader(null);
    setModal(true);
    setModalType("END");
    if (end) end({ statistics: gameStats });
    if (gameStatus.status === 3) {
      setDrawGame(true);
    } else {
      setPlayerWin(gameStatus.status);
      setWinningCoords(gameStatus.winningCoords);
    }
  };

  const handleBoardClick = (row, col) => {
    setLock(true);
    setPlayersMoves((prevPlayerMove) => prevPlayerMove + 1);
    setStepTimer({
      ...stepTimer,
      [`step-${playerMoves}`]: new Date() - initialTime,
    });
    const currentBoard = board.slice();
    let currPlayer = nextTurn(lastPlayer);
    const success = addCoinToMatrix(currentBoard, col, currPlayer);
    if (!success) {
      return afterGameOver(
        currentBoard,
        GameStatus(currentBoard, colSize, config.numRows, config.winCount)
      );
    }
    const nextPlayer = nextTurn(currPlayer);
    let currentGameStatus = GameStatus(
      currentBoard,
      colSize,
      config.numRows,
      config.winCount
    );
    if (currentGameStatus.status !== 0) {
      return afterGameOver(currentBoard, currentGameStatus);
    }
    setHeader(`👩‍💻It's Jane's turn`);
    setTimeout(() => {
      computerStep(currentBoard, nextPlayer, difficulty);
      currentGameStatus = GameStatus(
        currentBoard,
        colSize,
        config.numRows,
        config.winCount
      );
      if (currentGameStatus.status !== 0) {
        return afterGameOver(currentBoard, currentGameStatus);
      }
      setLastPlayer(nextPlayer);
      setBoard(currentBoard);
      setLock();
      setHeader(`It's your turn 😊`);
      setInitialTime(new Date());
    }, 2000);
  };

  const shareHandler = () => {
    navigator
      .share(share)
      .then(() => {
        end({ statistics: { shared: true } });
        console.log("Thanks for sharing!");
      })
      .catch(console.error);
  };

  const getIsCircleInWinningState = (x, y) => {
    for (let i = 0; i < winningCoords.length; i += 1) {
      if (winningCoords[i].x === x && winningCoords[i].y === y) {
        return true;
      }
    }
    return false;
  };

  return (
    <>
      <Modal
        feature={feature}
        show={modal}
        type={modalType}
        kind={"CONNECT4"}
        intro={intro}
        difficulty={
          <div className="connect4-difficulty-level">
            <div className="connect4-difficulty-level-header">
              <h2>Select Level</h2>
            </div>
            <div className="connect4-all-level">
              {LEVELS.map((entry, entryIndex) =>
                difficulty === entryIndex + 1 ? (
                  <div onClick={() => setDifficulty(entryIndex + 1)}>
                    <div class="selected-box">
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          backgroundColor: "#2E2E2E",
                        }}
                      >
                        {entry}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => setDifficulty(entryIndex + 1)}
                    className="slots therapy-modes"
                  >
                    {entry}
                  </div>
                )
              )}
            </div>
          </div>
        }
        stats={{
          outcome: drawGame ? "DRAW" : playerWin === 1 ? "WIN" : "LOSE",
        }}
        done={() => {
          if (modalType === "START") {
            if (start) start(playAgain ? "PLAY_AGAIN" : "START");
            setModal(false);
            setModalType("");
          } else if (modalType === "END") {
            setModal(false);
            setModalType("");
            onDone();
          }
        }}
        reset={() => {
          setModal(false);
          setModalType("");
          reset();
        }}
        shareable={isMobile()}
        win={!drawGame && playerWin === 1}
        share={share}
      />
      <div className="connect4-section" style={{ position: "relative" }}>
        <div className="connect4-header-audio">
          <div className="connect4-ai-header">{header}</div>
          <div className="connect4-audio-box" onClick={toggleAudio}>
            {audio ? (
              <div className="connect4-audio-control">
                <img
                  src="https://cdn.mindpeers.co/dark-mode/site-icons/volume_play.svg"
                  alt="AUDIO_PLAY"
                />
              </div>
            ) : (
              <div className="connect4-audio-control">
                <img
                  src="https://cdn.mindpeers.co/site-icons/volume_mute.svg"
                  alt="AUDIO_PAUSE"
                />
              </div>
            )}
          </div>
        </div>
        <div className="connect4">
          <div className="connect4-coin-container">
            {board.map((row, rowIndex) => (
              <div key={`row${rowIndex}`} className="connect4-board-row">
                {row.map((column, columnIndex) => (
                  <div className="connect4-board-body">
                    <div
                      key={`column${columnIndex}`}
                      className={`connect4-board-column ${
                        getIsCircleInWinningState(rowIndex, columnIndex)
                          ? "connect4-blink"
                          : ""
                      }`}
                      onClick={() => {
                        if (!gameDone && !lock)
                          handleBoardClick(rowIndex, columnIndex);
                      }}
                    >
                      <div id={`${rowIndex}-${columnIndex}`}></div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Connect;
